import {
  Box,
  Button,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@material-ui/core';
import { Field, FormikProps } from 'formik';
import React, { FunctionComponent, useCallback, useRef, useState } from 'react';
import PersistenceForm from '../../../../Modals/PersistenceModal/PersistenceForm';
import useStyles from '../../../../Modals/PersistenceModal/PersistenceForm/styles';
import useAddStatistics from './hook';
import { schema } from './schema';
import { FIELDS } from './fields';
import {
  EDocumentType,
  useAddDocumentToPatientCareEpisodeMutation,
} from 'src/domains/medical-record/repository';

interface StatisticCreationFormProps {
  onClose: () => void;
}

export interface StatisticCreationFormInterface {
  cholesterol: string;
  diastolicBp: string;
  glycemia: string;
  hdl: string;
  ldl: string;
  systolicBp: string;
  timeElapsedAfterMeal: string;
  triglycerides: string;
  waistSize: string;
  weight: string;
}

const DEFAULT_FORM = {
  cholesterol: '',
  diastolicBp: '',
  glycemia: '',
  hdl: '',
  ldl: '',
  systolicBp: '',
  timeElapsedAfterMeal: '',
  triglycerides: '',
  waistSize: '',
  weight: '',
};

const StatisticCreationForm: FunctionComponent<StatisticCreationFormProps> = ({ onClose }) => {
  const classes = useStyles();
  const [files, setFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { isSuccess, error, add } = useAddStatistics();

  const [
    addDocumentToPatientCareEpisode,
    {
      isLoading: isDocumentAddLoading,
      isSuccess: isDocumentAddSuccess,
      isError: isDocumentAddError,
    },
  ] = useAddDocumentToPatientCareEpisodeMutation();

  const onSubmit = useCallback(
    async (data: StatisticCreationFormInterface) => {
      setIsLoading(true);
      const cleanedData = Object.entries(data).reduce((acc, [key, value]) => {
        if (value === '') {
          return acc;
        }
        return {
          ...acc,
          [key]: value,
        };
      }, {} as StatisticCreationFormInterface);
      console.log('data', cleanedData);
      await add(cleanedData);

      //save documents
      for (const file of files) {
        console.log('file', file);
        await addDocumentToPatientCareEpisode({
          document: { type: EDocumentType.CLINICAL_RESULT },
          files: [file],
        });
      }
      setIsLoading(false);
    },
    [files, add, addDocumentToPatientCareEpisode],
  );

  const removeFile = (indexToRemove: number) => {
    setFiles(prevFiles => prevFiles.filter((_, index) => index !== indexToRemove));
  };

  return (
    <PersistenceForm
      error={error}
      initialValues={DEFAULT_FORM}
      isUpdateMode={false}
      isCreating={isLoading}
      isSubmittingSuccessfully={isSuccess || isDocumentAddSuccess}
      onClose={onClose}
      onSubmit={onSubmit}
      validationSchema={schema}
      formContent={(props: FormikProps<StatisticCreationFormInterface>) => (
        <>
          {FIELDS.map(({ key, label, unit }) => (
            <div key={key} className={classes.fieldRow}>
              <div className={classes.fieldLabelContainer}>
                <Typography variant={'body2'}>{label}: </Typography>
              </div>
              <div className={classes.inputContainer}>
                <Field
                  value={props.values[key]}
                  margin={'dense'}
                  variant={'outlined'}
                  color={'primary'}
                  id="value"
                  name="value"
                  placeholder=""
                  type="number"
                  error={!!props.errors[key]}
                  helperText={props.errors[key]}
                  InputProps={{
                    endAdornment: <InputAdornment position="start">{unit.label}</InputAdornment>,
                  }}
                  onChange={(
                    event: React.ChangeEvent<{ name: string | undefined; value: number }>,
                  ) => {
                    props.setFieldValue(key, event.target.value, true);
                  }}
                  onWheel={(e: React.WheelEvent<HTMLInputElement>) => {
                    (e.target as HTMLInputElement).blur();
                  }}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                      e.preventDefault();
                    }
                  }}
                  component={TextField}
                />
              </div>
            </div>
          ))}
          <div>
            <input
              ref={fileInputRef}
              id="files"
              name="files"
              type="file"
              accept="application/pdf, image/jpeg, image/png"
              multiple
              style={{ display: 'none' }}
              onChange={event => {
                setFiles([
                  ...files,
                  ...Array.from(event.currentTarget.files ? event.currentTarget.files : []),
                ]);
              }}
            />
            {files.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <Typography>Fichier(s) sélectionné(s) : </Typography>
                <List>
                  {files.map((file, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={file.name} />
                      <Button
                        aria-label="delete"
                        variant="outlined"
                        onClick={() => removeFile(index)}
                      >
                        Supprimer
                      </Button>
                      <Divider />
                    </ListItem>
                  ))}
                </List>
              </div>
            )}
            <Box display="flex" justifyContent="flex-end" mt={2} mb={2}>
              <Button
                style={{ marginTop: '16px', marginBottom: '16px' }}
                variant="contained"
                color="primary"
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
              >
                Ajouter un document
              </Button>
            </Box>
            {!isDocumentAddLoading && isDocumentAddError && (
              <Typography>Erreur lors de la sauvegarde des documents</Typography>
            )}
          </div>
        </>
      )}
    />
  );
};

export default StatisticCreationForm;
